/**
 * DetailingWorkspace — AISC §1.7.0 + D-032 unified workspace screen.
 *
 * Mirrors the v2 wireframe at
 * `outputs/a3sfm-build/aisc-detailing-1.7.0-design/aisc-detailing-1.7.0-wireframe-v2.html`.
 *
 * Tabs: Overview / Fabricated Items / RFIs / Transmittals / Print Issue Tickets / Customer Waivers / Activity Log.
 * Overview + Fabricated Items render inline; other tabs link out to existing pages until in-tab content lands in a follow-up.
 */

import React, { useEffect, useState } from "react";
import PageGuide from "../../components/PageGuide";
import { prettyStatus } from "../../components/PaperForm/styles";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { hasNavPermission } from "../../utils/hasPermission";
import {
  Container, Box, Stack, Typography, Paper, Chip, Button, Alert,
  Breadcrumbs, Link as MuiLink, Divider, Tabs, Tab, Grid,
  Table, TableBody, TableRow, TableCell, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, IconButton,
} from "@mui/material";
import {
  HomeRounded, ChevronRightRounded, AddRounded, ReplyRounded,
  PauseCircleOutlineRounded, NotificationsActiveRounded, OpenInNewRounded,
  TrendingUpRounded, TrendingDownRounded, TrendingFlatRounded,
  EditOutlined,
} from "@mui/icons-material";

import { getDetailingWorkspace, setWorkflowStep, WORKFLOW_STEPS, WORKFLOW_STEP_LABELS } from "../../utils/detailingWorkspaceApi";
import { setJobCategory, FABRICATION_CATEGORIES } from "../../utils/jobCategoryApi";
import WorkflowStepDialog from "../../components/WorkflowStepDialog/WorkflowStepDialog";
// Route merge 2026-05-05: Fabricated Items tab embeds the legacy /detail-Job/:id page
import Detail from "./Detail.jsx";
import MaterialTraceabilityControl from "../../components/MaterialTraceability/MaterialTraceabilityControl"; // CR-2026-TRC-GATE

const STATUS_PILL = {
  JOB_CREATED:        { bg: "#e9ecef", color: "#6c757d", label: "Job Created" },
  DETAILER_ASSIGNED:  { bg: "#cfe2ff", color: "#052c65", label: "Detailer Assigned" },
  DRAWINGS_COMPLETE:  { bg: "#cfe2ff", color: "#052c65", label: "Drawings Complete" },
  CATEGORY_SET:       { bg: "#cfe2ff", color: "#052c65", label: "Category Set" },
  AWAITING_CUSTOMER:  { bg: "#fef3c7", color: "#d97706", label: "Awaiting Customer Return" },
  APPROVED:           { bg: "#d1e7dd", color: "#198754", label: "Approved" },
  RELEASED_FOR_FAB:   { bg: "#198754", color: "#ffffff", label: "Released for Fab" },
};

const RFI_STATUS_COLOR = {
  DRAFT:     { bg: "#e9ecef", color: "#6c757d" },
  SENT:      { bg: "#cfe2ff", color: "#052c65" },
  ANSWERED:  { bg: "#d1e7dd", color: "#0a3622" },
  CLOSED:    { bg: "#198754", color: "#ffffff" },
  CANCELLED: { bg: "#f8d7da", color: "#58151c" },
};
const TR_STATUS_COLOR = {
  DRAFT:     { bg: "#e9ecef", color: "#6c757d" },
  SENT:      { bg: "#cfe2ff", color: "#052c65" },
  RETURNED:  { bg: "#fef3c7", color: "#d97706" },
  CLOSED:    { bg: "#198754", color: "#ffffff" },
  CANCELLED: { bg: "#f8d7da", color: "#58151c" },
};

// Tab indices — keep in sync with the <Tabs> declaration below
// Route merge 2026-05-05: index 1 is now Fabricated Items (was RFIs); subsequent tabs +1
const TAB_OVERVIEW = 0;
const TAB_FABRICATED_ITEMS = 1;
const TAB_RFIS = 2;
const TAB_TRANSMITTALS = 3;
const TAB_PRINT_TICKETS = 4;
const TAB_CUSTOMER_WAIVERS = 5;
const TAB_ACTIVITY_LOG = 6;
const TAB_CONTRACT_REVIEW = 7; // CR-2026-CONTRACT-REVIEW (2026-10-15): F1.6.0 opens its own page

const TAB_NAME_TO_INDEX = {
  "overview": TAB_OVERVIEW,
  "fabricated-items": TAB_FABRICATED_ITEMS,
  "rfis": TAB_RFIS,
  "transmittals": TAB_TRANSMITTALS,
  "print-issue-tickets": TAB_PRINT_TICKETS,
  "customer-waivers": TAB_CUSTOMER_WAIVERS,
  "activity-log": TAB_ACTIVITY_LOG,
  "contract-review": TAB_CONTRACT_REVIEW,
};

export default function DetailingWorkspace() {
  // CR-2026-CONTRACT-REVIEW-R1 (review P3-12): the F1.6.0 tab is offered only to users who may view contract reviews.
  const crPerms = useSelector((state) => state.userSlice?.loginUser?.decodedToken?.permissions) || [];
  const canViewContractReview = hasNavPermission(crPerms, ["view_contract_review"]); // same check as PermissionGuard
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // 2026-05-05 (Dean QA report bugs #6 + #7) — Send Reminder + Place Hold
  // had no onClick handlers, no toast, no state change. Adding a single
  // workspace-level Snackbar fed by stub handlers so the user sees an
  // explicit "logged" confirmation. Backend wiring (audit_log writes,
  // notification dispatch, hold-state transition on jobs.status) is a
  // separate AP-class CR — see CR-2026-BE-JOB-HOLD-STATE in queue.
  const [wsToast, setWsToast] = useState(null); // { message, severity }
  // CR-2026-FE-WORKFLOW-OVERRIDE 2026-05-05 — Edit Timeline (Option A)
  const [overrideTarget, setOverrideTarget] = useState(null);   // step token or null
  const [overrideSubmitting, setOverrideSubmitting] = useState(false);
  const [overrideError, setOverrideError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // 2026-05-06 BUG 1 fix — Set Fabrication Category dialog state
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categoryValue, setCategoryValue] = useState("");
  const [categoryRationale, setCategoryRationale] = useState("");
  const [categorySubmitting, setCategorySubmitting] = useState(false);
  const [categoryError, setCategoryError] = useState(null);
  // Initial tab from ?tab= URL param (e.g. /jobs/1/detailing?tab=fabricated-items)
  const [tab, setTab] = useState(() => {
    const t = searchParams.get("tab");
    return TAB_NAME_TO_INDEX[t] ?? TAB_OVERVIEW;
  });

  // Keep URL ?tab= in sync when user clicks a tab
  const handleTabChange = (_, newTab) => {
    setTab(newTab);
    const tabName = Object.keys(TAB_NAME_TO_INDEX).find((k) => TAB_NAME_TO_INDEX[k] === newTab);
    if (tabName && tabName !== "overview") {
      setSearchParams({ tab: tabName });
    } else {
      setSearchParams({});
    }
  };

  useEffect(() => {
    setLoading(true); setError(null);
    getDetailingWorkspace(id)
      .then(setData)
      .catch((err) => setError(err?.response?.data?.error || err?.message))
      .finally(() => setLoading(false));
  }, [id, refreshKey]);

  // CR-2026-FE-WORKFLOW-OVERRIDE 2026-05-05 — confirm dialog handler
  const handleOverrideConfirm = async (reason) => {
    if (!overrideTarget) return;
    setOverrideSubmitting(true);
    setOverrideError(null);
    const result = await setWorkflowStep(id, overrideTarget, reason);
    setOverrideSubmitting(false);
    if (result.ok) {
      const stepLabel = WORKFLOW_STEP_LABELS[overrideTarget] || overrideTarget;
      setOverrideTarget(null);
      setWsToast({ message: `Workflow step set to "${stepLabel}". Captured in audit_log.`, severity: "success" });
      setRefreshKey((k) => k + 1);
    } else {
      setOverrideError(result.error);
    }
  };

  if (loading) return <Container sx={{ py: 4 }}><Typography>Loading workspace…</Typography></Container>;
  if (error) return <Container sx={{ py: 4 }}><Alert severity="error">{error}</Alert></Container>;
  if (!data) return <Container sx={{ py: 4 }}><Alert severity="warning">No workspace data.</Alert></Container>;

  const job = data.job || {};
  const wf = data.workflow_state || { current: "JOB_CREATED", completed: [], pending: [] };
  const pill = STATUS_PILL[wf.current] || STATUS_PILL.JOB_CREATED;
  const kpis = data.kpis || {};
  const rfis = data.recent_rfis || [];
  const trs = data.recent_transmittals || [];

  return (
    <>
      <Helmet><title>{`${job.name || `Job ${job.id}`} — Detailing | SFM`}</title></Helmet>
      <Container maxWidth="lg" sx={{ py: 3 }}>

        {/* Breadcrumbs — 2026-05-06 Dean directive: drop Home; start at Jobs */}
        <Breadcrumbs sx={{ mb: 2 }} separator={<ChevronRightRounded fontSize="small" />}>
          <MuiLink href="/jobs" color="inherit" underline="hover">Jobs</MuiLink>
          <MuiLink href={`/jobs/${id}`} color="inherit" underline="hover">
            {job.job_number ? `${job.job_number} — ${job.name || ""}` : (job.name || `Job ${id}`)}
          </MuiLink>
          <Typography color="text.primary">Detailing Workspace</Typography>
        </Breadcrumbs>
        <PageGuide
          why="Run a job's detailing in one place — drawings, sequences, RFIs, transmittals — so nothing slips between handoffs."
          who="Detailers and PMs, running a job's detailing and handoffs."
          what="The detailing hub for a job: its drawing packages, sequences, and document workflow."
          how="Work through the job's drawings and sequences; raise RFIs and send transmittals from here."
          when="Throughout detailing, from first drawings to release for fabrication."
          standard="AISC 303 §1.7.0"
        />

        {/* Job header card */}
        <Paper sx={{ p: 3, mb: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} spacing={2}>
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                <Box component="span" sx={{ color: "text.secondary", fontWeight: 500, mr: 1 }}>
                  {job.job_number || `#${job.id}`}
                </Box>
                {job.name || "(unnamed job)"}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                {[job.customer_name, job.phase, job.piece_count ? `${job.piece_count} piece-marks` : null].filter(Boolean).join(" · ") || "—"}
              </Typography>
            </Box>
            <Chip label={pill.label} sx={{ bgcolor: pill.bg, color: pill.color, fontWeight: 600, flexShrink: 0 }} />
          </Stack>

          <Divider sx={{ my: 2 }} />

          {/* CR-2026-FE-NAMECARD-HIDE-EMPTY 2026-05-06 — only render cells that
              have data, OR cells that have a meaningful empty-state action.
              Sheets is always shown (always has a value from fabricated_items).
              Issued / Addendas hide when null (no schema backing yet — those
              columns don't exist on jobs table; cells are placeholders for a
              future migration). FabCat shows even when null (operator can
              click to set via PATCH /api/job/:id/category). Detailer shows
              even when null (link to /detailer-qualifications). */}
          <Grid container spacing={3}>
            <Grid item xs={6} md={2.4}>
              <MetaCell label="Drawings" value={`${data.drawing_count || 0} sheets`}
                sub={job.drawing_range || null} />
            </Grid>
            {job.issued_at && (
              <Grid item xs={6} md={2.4}>
                <MetaCell label="Issued" value={new Date(job.issued_at).toISOString().slice(0, 10)}
                  sub={job.issue_volumes ? `${job.issue_volumes} volumes` : null} />
              </Grid>
            )}
            {(job.addenda_count || job.addenda_list) && (
              <Grid item xs={6} md={2.4}>
                <MetaCell label="Addendas" value={job.addenda_count ? `${job.addenda_count} active` : "—"}
                  sub={job.addenda_list || null} />
              </Grid>
            )}
            <Grid item xs={6} md={2.4}>
              <MetaCell label="Fabrication Category"
                value={
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {job.fabrication_category
                      ? <Chip size="small" label={job.fabrication_category} sx={{ bgcolor: "#e7f1ff", color: "#0066cc", fontWeight: 600 }} />
                      : <MuiLink
                          component="button"
                          onClick={() => {
                            setCategoryValue(job.fabrication_category || "");
                            setCategoryRationale("");
                            setCategoryError(null);
                            setCategoryDialogOpen(true);
                          }}
                          sx={{ fontSize: "0.875rem", fontStyle: "italic", color: "primary.main" }}
                        >
                          Set category →
                        </MuiLink>}
                    {job.fabrication_category && (
                      <IconButton size="small" aria-label="Edit category"
                        onClick={() => {
                          setCategoryValue(job.fabrication_category);
                          setCategoryRationale("");
                          setCategoryError(null);
                          setCategoryDialogOpen(true);
                        }}>
                        <EditOutlined fontSize="inherit" />
                      </IconButton>
                    )}
                  </Stack>
                }
                sub={job.fabrication_category_set_at
                  ? `Set ${new Date(job.fabrication_category_set_at).toISOString().slice(0, 10)}`
                  : null} />
            </Grid>
            <Grid item xs={12} md={4.8}>
              <MetaCell label="Material traceability (contract)" value={<MaterialTraceabilityControl jobId={job.id} />}
                sub={<MuiLink component="button" onClick={() => navigate(`/piece-labels?jobId=${job.id}`)}>Print piece labels</MuiLink>} />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <MetaCell label="Detailer"
                value={data.detailer?.detailer_name || data.detailer?.party_name ||
                  <MuiLink
                    component="button"
                    onClick={() => navigate(`/detailer-qualifications?job_id=${id}`)}
                    sx={{ fontSize: "0.875rem", fontStyle: "italic", color: "primary.main" }}
                  >
                    Assign detailer →
                  </MuiLink>}
                sub={data.detailer ? "✓ qualifications current" : null} />
            </Grid>
          </Grid>
        </Paper>

        {/* CR-2026-FE-DETAILING-QUICK-ACTIONS 2026-05-06 — quick action buttons.
            Most-common operations from this screen: upload drawings + manage
            detailer qualifications. Codex F3 fix 2026-05-07: only the Upload
            Documents link can actually pre-fill from the query string —
            DocumentUpload now reads ?job_id= via useSearchParams. The
            "Manage Detailer Qualifications" link goes to the tenant-wide
            qualification register (qualifications are per-detailer/contractor,
            not per-job in the current schema), so we don't pretend to
            pre-fill — the user picks the qualification record and links it
            to a job downstream. */}
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddRounded />}
            onClick={() => navigate(`/documents/upload?job_id=${id}`)}
          >
            Upload Documents
          </Button>
          {!data.detailer && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddRounded />}
              onClick={() => navigate(`/detailer-qualifications`)}
            >
              Manage Detailer Qualifications
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<OpenInNewRounded />}
            onClick={() => navigate(`/jobs/${id}/activity-log`)}
          >
            View Activity
          </Button>
        </Stack>

        {/* Tabs — Fabricated Items tab added 2026-05-05 (route merge from /detail-Job/:id) */}
        <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}>
          <Tab label="Overview" />
          <Tab label="Fabricated Items" />
          <Tab label={<Stack direction="row" spacing={1} alignItems="center"><span>RFIs</span><Chip size="small" label={rfis.length} /></Stack>} />
          <Tab label={<Stack direction="row" spacing={1} alignItems="center"><span>Transmittals</span><Chip size="small" label={trs.length} /></Stack>} />
          <Tab label={<Stack direction="row" spacing={1} alignItems="center"><span>Print Issue Tickets</span><Chip size="small" label={kpis.print_tickets_this_week || 0} /></Stack>} />
          <Tab label="Customer Waivers" />
          <Tab label="Activity Log" />
          <Tab label="Contract Review (F1.6.0)" disabled={!canViewContractReview} />
        </Tabs>

        {/* Fabricated Items tab — embeds legacy Detail.jsx (Add Item, Heats, Kiosk View,
            Release Packages, Upload Drawing PDF buttons + Fabricated Items table +
            PurchaseOrder Items + Drawings viewer + PDF upload) */}
        {tab === TAB_FABRICATED_ITEMS && (
          <Box sx={{ mt: 2 }}>
            <Detail embedded />
          </Box>
        )}

        {/* Overview tab */}
        {tab === 0 && (
          <>
            {/* Workflow rail */}
            <Paper sx={{ p: 3, mb: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Detailing workflow state</Typography>
                <Button size="small" variant="outlined" onClick={() => navigate(`/jobs/${id}/activity-log`)}>
                  View timeline
                </Button>
              </Stack>

              <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", overflowX: "auto", pb: 1 }}>
                {WORKFLOW_STEPS.map((step, i) => {
                  const isComplete = wf.completed?.includes(step);
                  const isCurrent = wf.current === step;
                  const stepLabel = WORKFLOW_STEP_LABELS[step] || step;
                  const sx = isCurrent
                    ? { bgcolor: "primary.main", color: "white", boxShadow: "0 0 0 3px rgba(0,102,204,.2)" }
                    : isComplete
                      ? { bgcolor: "#d1e7dd", color: "#198754" }
                      : { bgcolor: "#f0f3f7", color: "text.secondary" };
                  return (
                    <React.Fragment key={step}>
                      {/* CR-2026-FE-WORKFLOW-OVERRIDE 2026-05-05 — Chip is now
                          clickable. Click opens the WorkflowStepDialog with
                          this step as the target. The dialog handles the
                          confirm + reason + POST. Cursor + hover give a
                          visual affordance that the chip is interactive. */}
                      <Chip
                        label={stepLabel}
                        clickable
                        onClick={() => { setOverrideError(null); setOverrideTarget(step); }}
                        sx={{
                          fontWeight: 600,
                          flexShrink: 0,
                          cursor: "pointer",
                          transition: "transform .12s ease, box-shadow .12s ease",
                          "&:hover": isCurrent
                            ? { boxShadow: "0 0 0 4px rgba(0,102,204,.3)" }
                            : { transform: "translateY(-1px)", boxShadow: "0 2px 6px rgba(0,0,0,.08)" },
                          ...sx,
                        }}
                      />
                      {i < WORKFLOW_STEPS.length - 1 && (
                        <Typography component="span" sx={{ color: "text.disabled", flexShrink: 0 }}>›</Typography>
                      )}
                    </React.Fragment>
                  );
                })}
              </Box>

              {/* CR-2026-FE-WORKFLOW-OVERRIDE 2026-05-05 — when the backend
                  reports is_override:true, surface a small banner so the
                  operator (and audit-day reviewer) can see the displayed
                  step is a manual override, not the auto-computed one. */}
              {wf.is_override && (
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mt: 1.5, py: 0.5, fontSize: "0.8rem", borderLeft: 3, borderColor: "#d97706" }}
                >
                  <strong>Manual override active.</strong> Step set by user. Auto-computed step
                  would be <strong>{WORKFLOW_STEP_LABELS[wf.computed_step] || wf.computed_step || "—"}</strong>.
                  Captured in audit_log. Click any step to change.
                </Alert>
              )}

              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1, fontStyle: "italic" }}>
                Click any step to set it manually (Edit Timeline). Each change is captured in audit_log per AISC §1.7.0.
              </Typography>

              {wf.narrative && (
                <Alert severity="info" icon={false}
                  sx={{ mt: 2, borderLeft: 3, borderColor: "primary.main", borderRadius: 1 }}>
                  {wf.narrative}
                </Alert>
              )}

              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                {wf.current === "AWAITING_CUSTOMER" && (
                  <Button variant="contained" startIcon={<ReplyRounded />}
                    onClick={() => navigate(`/transmittals?job_id=${id}`)}>
                    Record Customer Return
                  </Button>
                )}
                {/* 2026-05-05 — bug #6 + #7 fix: Send Reminder + Place Hold
                    were buttons with no onClick. Stub handlers fire a toast
                    so the user gets confirmation; full backend wiring
                    (audit_log + notification dispatch + jobs.status hold
                    state transition) is pending in CR-2026-BE-JOB-HOLD-STATE.
                    2026-05-06 BUG 5 fix — disable both buttons while
                    WorkflowStepDialog or Category dialog is open so stray
                    click pass-through (Cancel button overlapping screen
                    position) cannot accidentally trigger Place Hold. */}
                <Button
                  variant="outlined"
                  startIcon={<NotificationsActiveRounded />}
                  disabled={Boolean(overrideTarget) || categoryDialogOpen}
                  onClick={() =>
                    setWsToast({
                      message: `Reminder logged for "${job?.name || `Job #${id}`}". Backend dispatch lands in CR-2026-BE-JOB-NOTIFY (queued).`,
                      severity: "info",
                    })
                  }
                >
                  Send Reminder
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<PauseCircleOutlineRounded />}
                  disabled={Boolean(overrideTarget) || categoryDialogOpen}
                  onClick={() =>
                    setWsToast({
                      message: `Hold request logged for "${job?.name || `Job #${id}`}". State transition lands in CR-2026-BE-JOB-HOLD-STATE (queued).`,
                      severity: "warning",
                    })
                  }
                >
                  Place Hold
                </Button>
              </Stack>
            </Paper>

            {/* KPI grid */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6} md={3}>
                <KpiCard label="Open RFIs" value={kpis.open_rfis ?? 0}
                  trend="up" trendLabel="awaiting EOR" valueColor="#d97706"
                  onClick={() => handleTabChange(null, TAB_RFIS)} />
              </Grid>
              <Grid item xs={6} md={3}>
                <KpiCard label="Pending Transmittals" value={kpis.pending_transmittals ?? 0}
                  trend="flat" trendLabel="no change" valueColor="#0066cc"
                  onClick={() => handleTabChange(null, TAB_TRANSMITTALS)} />
              </Grid>
              <Grid item xs={6} md={3}>
                <KpiCard label="Print Tickets This Week" value={kpis.print_tickets_this_week ?? 0}
                  trend="up" trendLabel="vs last week" valueColor="#198754"
                  onClick={() => handleTabChange(null, TAB_PRINT_TICKETS)} />
              </Grid>
              <Grid item xs={6} md={3}>
                <KpiCard label="Drawing Checks Outstanding" value={kpis.outstanding_checks ?? 0}
                  trend="down" trendLabel="this week" valueColor="#dc2626"
                  onClick={() => navigate("/drawing-checks")} />
              </Grid>
            </Grid>

            {/* Two-column: RFIs + Transmittals previews */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <Paper sx={{ p: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Recent RFIs</Typography>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" variant="contained" startIcon={<AddRounded />}
                        onClick={() => navigate(`/rfis/new?job_id=${id}`)}>New RFI</Button>
                      <Button size="small" variant="outlined" endIcon={<OpenInNewRounded />}
                        onClick={() => navigate(`/rfis?job_id=${id}`)}>View All</Button>
                    </Stack>
                  </Stack>
                  <Table size="small">
                    <TableBody>
                      {!rfis.length && (
                        <TableRow><TableCell colSpan={4} sx={{ color: "text.secondary", py: 3, textAlign: "center" }}>
                          No RFIs on this job yet. Click "New RFI" to create one.
                        </TableCell></TableRow>
                      )}
                      {rfis.map((rfi) => {
                        const c = RFI_STATUS_COLOR[rfi.status] || RFI_STATUS_COLOR.DRAFT;
                        return (
                          <TableRow key={rfi.id} hover sx={{ cursor: "pointer" }}
                            onClick={() => navigate(`/rfis/${rfi.id}`)}>
                            <TableCell sx={{ width: 80, fontFamily: "monospace", fontWeight: 600, color: "primary.main" }}>
                              {rfi.rfi_number}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ fontSize: 13.5 }}>
                                {(rfi.information_requested || "").slice(0, 60)}
                                {(rfi.information_requested || "").length > 60 ? "…" : ""}
                              </Box>
                              <Box sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>
                                Requested of {rfi.requested_of_party}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip size="small" label={prettyStatus(rfi.status)}
                                sx={{ bgcolor: c.bg, color: c.color, fontWeight: 600 }} />
                            </TableCell>
                            <TableCell sx={{ fontSize: 12, color: "text.secondary", whiteSpace: "nowrap" }}>
                              {rfi.created_at ? new Date(rfi.created_at).toISOString().slice(0, 10) : "—"}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>

              <Grid item xs={12} md={5}>
                <Paper sx={{ p: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Active Transmittals</Typography>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" variant="contained" startIcon={<AddRounded />}
                        onClick={() => navigate(`/transmittals/new?job_id=${id}`)}>New</Button>
                    </Stack>
                  </Stack>
                  <Table size="small">
                    <TableBody>
                      {!trs.length && (
                        <TableRow><TableCell colSpan={3} sx={{ color: "text.secondary", py: 3, textAlign: "center" }}>
                          No transmittals yet. Click "New" to send drawings to a customer.
                        </TableCell></TableRow>
                      )}
                      {trs.map((tr) => {
                        const c = TR_STATUS_COLOR[tr.status] || TR_STATUS_COLOR.DRAFT;
                        return (
                          <TableRow key={tr.id} hover sx={{ cursor: "pointer" }}
                            onClick={() => navigate(`/transmittals/${tr.id}`)}>
                            <TableCell sx={{ width: 80, fontFamily: "monospace", fontWeight: 600, color: "primary.main" }}>
                              {tr.transmittal_number}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ fontSize: 13.5 }}>{tr.recipient_party}</Box>
                              <Box sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>
                                {tr.sent_at ? `Sent ${new Date(tr.sent_at).toISOString().slice(0, 10)}` : `Created ${new Date(tr.created_at).toISOString().slice(0, 10)}`}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip size="small" label={prettyStatus(tr.status)}
                                sx={{ bgcolor: c.bg, color: c.color, fontWeight: 600 }} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
            </Grid>
          </>
        )}

        {/* Other tabs — link out to full pages until in-tab content lands in a follow-up */}
        {tab > TAB_FABRICATED_ITEMS && (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {["Overview", "Fabricated Items", "RFIs", "Transmittals", "Print Issue Tickets", "Customer Waivers", "Activity Log", "The F1.6.0 contract review"][tab]} for this job open in their dedicated page.
            </Typography>
            {tab === TAB_CONTRACT_REVIEW && !canViewContractReview && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>You do not have permission to view contract reviews.</Typography>
            )}
            <Button variant="contained" endIcon={<OpenInNewRounded />} disabled={tab === TAB_CONTRACT_REVIEW && !canViewContractReview}
              onClick={() => {
                const paths = ["", "", `/rfis?job_id=${id}`, `/transmittals?job_id=${id}`,
                  `/print-issue-tickets?job_id=${id}`, `/customer-waivers?job_id=${id}`,
                  `/jobs/${id}/activity-log`, `/jobs/${id}/contract-review`];
                navigate(paths[tab]);
              }}>
              Open
            </Button>
          </Paper>
        )}

        <Box sx={{ mt: 3, color: "text.secondary", fontSize: 12 }}>
          Per AISC §1.7.0 Detailing. Each tab opens its dedicated page for RFIs, Transmittals, Print Issue Tickets, Waivers, and the Activity Log.
        </Box>
      </Container>

      {/* 2026-05-05 — bugs #6 + #7 toast */}
      <Snackbar
        open={!!wsToast}
        autoHideDuration={5000}
        onClose={() => setWsToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {wsToast ? (
          <Alert
            onClose={() => setWsToast(null)}
            severity={wsToast.severity}
            sx={{ width: "100%" }}
          >
            {wsToast.message}
          </Alert>
        ) : null}
      </Snackbar>

      {/* CR-2026-FE-WORKFLOW-OVERRIDE 2026-05-05 — Edit Timeline dialog */}
      <WorkflowStepDialog
        open={Boolean(overrideTarget)}
        currentStep={wf.current}
        targetStep={overrideTarget}
        onCancel={() => { if (!overrideSubmitting) { setOverrideTarget(null); setOverrideError(null); } }}
        onConfirm={handleOverrideConfirm}
        submitting={overrideSubmitting}
        error={overrideError}
      />

      {/* 2026-05-06 BUG 1 fix — Set Fabrication Category dialog (AISC §1.7.0 §3.2) */}
      <Dialog
        open={categoryDialogOpen}
        onClose={() => { if (!categorySubmitting) setCategoryDialogOpen(false); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Set Fabrication Category</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            Per AISC §1.7.0 §3.2, classify this job's fabrication complexity. Operations may revise
            the category through this dialog with a written rationale; every change is captured in
            the audit log under <code>AISC-1-7-0:JOB-CATEGORY-SET</code>.
          </Typography>
          {categoryError && <Alert severity="error" sx={{ mb: 2 }}>{categoryError}</Alert>}
          <TextField
            select
            fullWidth
            label="Fabrication Category"
            value={categoryValue}
            onChange={(e) => setCategoryValue(e.target.value)}
            sx={{ mb: 2 }}
            required
          >
            <MenuItem value="">(Select)</MenuItem>
            {FABRICATION_CATEGORIES.map((c) => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            label="Rationale (optional, recommended for revisions)"
            value={categoryRationale}
            onChange={(e) => setCategoryRationale(e.target.value)}
            multiline
            minRows={2}
            placeholder="e.g., Connection complexity reviewed with EOR; Conventional per §3.2 criteria."
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => { if (!categorySubmitting) setCategoryDialogOpen(false); }}
            disabled={categorySubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!categoryValue || categorySubmitting}
            onClick={async () => {
              setCategorySubmitting(true);
              setCategoryError(null);
              try {
                await setJobCategory(id, categoryValue, categoryRationale);
                setCategoryDialogOpen(false);
                setRefreshKey((k) => k + 1);
                setWsToast({
                  message: `Fabrication Category set to ${categoryValue}`,
                  severity: "success",
                });
              } catch (err) {
                const msg = err?.response?.data?.error || err?.message || "Failed to set category";
                setCategoryError(msg);
              } finally {
                setCategorySubmitting(false);
              }
            }}
          >
            {categorySubmitting ? "Saving…" : "Save Category"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function MetaCell({ label, value, sub }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: ".5px", fontWeight: 600, fontSize: 11 }}>
        {label}
      </Typography>
      <Box sx={{ fontSize: 14, fontWeight: 500, mt: 0.5 }}>{value}</Box>
      {sub && (
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.25 }}>{sub}</Typography>
      )}
    </Box>
  );
}

function KpiCard({ label, value, trend, trendLabel, valueColor, onClick }) {
  const TrendIcon = trend === "up" ? TrendingUpRounded : trend === "down" ? TrendingDownRounded : TrendingFlatRounded;
  const trendBg = trend === "up" ? "#d1e7dd" : trend === "down" ? "#fee2e2" : "#f0f3f7";
  const trendColor = trend === "up" ? "#198754" : trend === "down" ? "#dc2626" : "#6c757d";
  return (
    <Paper sx={{ p: 2, cursor: "pointer", transition: "all .15s",
      "&:hover": { boxShadow: 4, transform: "translateY(-1px)" } }}
      onClick={onClick}>
      <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: ".4px", fontWeight: 600, fontSize: 11.5 }}>
        {label}
      </Typography>
      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 0.5, mb: 0.5 }}>
        <Typography sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1, color: valueColor }}>
          {value}
        </Typography>
        <Chip size="small" icon={<TrendIcon sx={{ fontSize: 14 }} />} label={trendLabel}
          sx={{ bgcolor: trendBg, color: trendColor, fontWeight: 600, height: 20, fontSize: 11,
            "& .MuiChip-icon": { color: "inherit", ml: 0.5 } }} />
      </Stack>
    </Paper>
  );
}
